/**
 * Simple PDF Upload Component
 *
 * Minimalist PDF file upload for campaign materials.
 * Files are stored in GCS for campaign planning.
 * Loads previously uploaded files from backend API on mount.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { auth } from '../../services/firebase';

interface UploadedPDF {
  filename: string;
  original_name: string;
  size_bytes: number;
  uploaded_at?: string;
  type?: string;  // "pdf" or "docx"
}

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function CandidateUpload() {
  const { user } = useAuth();

  const savedFilter = (() => {
    try {
      const raw = sessionStorage.getItem('phoenix_filter_state');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const districtCode: string | null = savedFilter?.municipalityCode || null;
  const targetYear: number = savedFilter?.year || 2026;
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedPDFs, setUploadedPDFs] = useState<UploadedPDF[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load uploaded PDFs from backend API on mount
  const loadUploadedPDFs = useCallback(async () => {
    if (!districtCode) {
      setIsLoadingList(false);
      return;
    }
    try {
      setIsLoadingList(true);
      const userParam = user?.uid ? `?user_id=${user.uid}` : '';
      const headers: HeadersInit = {};
      if (user && auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(
        `${API_BASE_URL}/api/analysis/candidate-pdfs/${districtCode}/${targetYear}${userParam}`,
        { headers }
      );
      if (response.ok) {
        const data = await response.json();
        const pdfs: UploadedPDF[] = (data.pdfs || []).map((pdf: { filename: string; size_bytes: number; uploaded_at?: string }) => ({
          filename: pdf.filename,
          original_name: pdf.filename,
          size_bytes: pdf.size_bytes,
          uploaded_at: pdf.uploaded_at,
        }));
        setUploadedPDFs(pdfs);
      }
    } catch {
      // Silently fail - user can still upload
    } finally {
      setIsLoadingList(false);
    }
  }, [user?.uid, districtCode, targetYear]);

  useEffect(() => {
    loadUploadedPDFs();
  }, [loadUploadedPDFs]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];

    for (const file of files) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const validExtensions = ['.pdf', '.docx'];
      const hasValidType = validTypes.includes(file.type);
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!hasValidType && !hasValidExtension) {
        setError(`"${file.name}" není PDF nebo DOCX soubor.`);
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        setError(`"${file.name}" je příliš velký (max 50 MB).`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError(null);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!districtCode) {
      setError('Nejprve vyberte obec v sekci Analýza.');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Vyberte alespoň jeden PDF soubor.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const uploaded: UploadedPDF[] = [];
    const failed: string[] = [];

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('district_code', districtCode);
        formData.append('election_type', 'komunalni');
        formData.append('target_year', String(targetYear));
        if (user?.uid) {
          formData.append('user_id', user.uid);
        }

        const uploadHeaders: HeadersInit = {};
        if (user && auth.currentUser) {
          const token = await auth.currentUser.getIdToken();
          uploadHeaders['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${API_BASE_URL}/api/analysis/upload-candidate-pdf`, {
          method: 'POST',
          headers: uploadHeaders,
          body: formData,
        });

        const data = await response.json();

        if (response.ok && data.success) {
          uploaded.push({
            filename: data.filename,
            original_name: data.original_name,
            size_bytes: data.size_bytes,
          });
        } else {
          failed.push(file.name);
        }
      } catch (err) {
        failed.push(file.name);
      }
    }

    setUploadedPDFs(prev => [...prev, ...uploaded]);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (uploaded.length > 0 && failed.length === 0) {
      setSuccess(`Úspěšně nahráno ${uploaded.length} soubor${uploaded.length > 1 ? 'ů' : ''}.`);
    } else if (uploaded.length > 0 && failed.length > 0) {
      setSuccess(`Nahráno ${uploaded.length} souborů. ${failed.length} selhalo.`);
    } else {
      setError('Nahrávání selhalo.');
    }

    setIsLoading(false);
  };

  const handleDeletePDF = async (filename: string) => {
    if (!districtCode) {
      setError('Nejprve vyberte obec v sekci Analýza.');
      return;
    }
    try {
      const userParam = user?.uid ? `?user_id=${user.uid}` : '';
      const deleteHeaders: HeadersInit = {};
      if (user && auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        deleteHeaders['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(
        `${API_BASE_URL}/api/analysis/candidate-pdfs/${districtCode}/${targetYear}/${filename}${userParam}`,
        { method: 'DELETE', headers: deleteHeaders }
      );
      if (response.ok) {
        setUploadedPDFs(prev => prev.filter(pdf => pdf.filename !== filename));
        setSuccess('Soubor smazán.');
      } else {
        setError('Smazání se nezdařilo.');
      }
    } catch {
      setError('Smazání se nezdařilo.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/filter"
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Zpět
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Nahrát dokumenty</h1>
        </div>

        {/* No-district warning banner */}
        {!districtCode && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              Nejprve <Link to="/filter" className="underline font-medium">vyberte obec v sekci Analýza</Link>, pak nahrávejte životopisy.
            </p>
          </div>
        )}

        {/* Upload Area */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileChange}
              className="hidden"
              multiple
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto text-gray-400 mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-gray-600 font-medium">
              Klikněte pro výběr PDF nebo WORD souborů
            </p>
            <p className="text-gray-400 text-sm mt-1">
              nebo přetáhněte soubory sem
            </p>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">
                  Vybrané soubory ({selectedFiles.length})
                </h3>
                <span className="text-sm text-gray-500">
                  {formatFileSize(totalSize)}
                </span>
              </div>
              <ul className="space-y-2 max-h-60 overflow-y-auto">
                {selectedFiles.map((file, index) => {
                  const isDocx = file.name.toLowerCase().endsWith('.docx');
                  return (
                  <li key={index} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 flex-shrink-0 ${isDocx ? 'text-blue-500' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="truncate text-gray-700">{file.name}</span>
                      <span className="text-gray-400 text-sm flex-shrink-0">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-red-500 ml-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Upload Button */}
          {selectedFiles.length > 0 && (
            <button
              onClick={handleUpload}
              disabled={isLoading || !districtCode}
              className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Nahrávám...
                </span>
              ) : (
                `Nahrát ${selectedFiles.length} soubor${selectedFiles.length > 1 ? 'ů' : ''}`
              )}
            </button>
          )}
        </div>

        {/* Uploaded Files (loaded from server) */}
        {isLoadingList ? (
          <div className="mt-6 bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
            Načítám nahrané soubory...
          </div>
        ) : uploadedPDFs.length > 0 ? (
          <div className="mt-6 bg-white rounded-lg shadow-md p-6">
            <h3 className="font-medium text-gray-900 mb-3">
              Nahrané soubory ({uploadedPDFs.length})
            </h3>
            <ul className="space-y-2">
              {uploadedPDFs.map((pdf, index) => (
                <li key={index} className="flex items-center justify-between text-gray-700">
                  <div className="flex items-center gap-3 min-w-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="truncate">{pdf.original_name}</span>
                    <span className="text-gray-400 text-sm flex-shrink-0">
                      {formatFileSize(pdf.size_bytes)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeletePDF(pdf.filename)}
                    className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                    title="Smazat soubor"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
